import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import {
  createMockAnswer,
  createMockAnswerV2,
  createLessonAnswer,
  createLessonAnswerV2,
} from "../../processes/answerProccess";
import { finishMock } from "../../processes/mockProccess";
import { goToIndex } from "../../processes/questionProccess";
import { getReplyAnswered, getAnswerForQuestionStem, getAnswerForLessonQuestionStem } from "../../selectors/answerSelector";
import {
  getCourseFromMock,
  hasMockCourses,
} from "../../selectors/courseSelectors";
import { getMock, makeIsComplete } from "../../selectors/mockSelectors";
import {
  getFlattenedQuestionsFromMock,
  makeCurrentQuestion,
  getCurrentQuestionAndStem,
} from "../../selectors/questionSelectors";
import {
  getFlattenedLessonQuestions,
  getCurrentLessonQuestionAndStem,
} from "../../selectors/lessonQuestionSelectors";
import { goToLessonQuestionIndex } from "../../processes/lessonQuestionProcess";
import QuestionNavigation from "./QuestionNavigation";
import { replace } from "connected-react-router";

function QuestionNavigationContainer(props) {
  const {
    currentIndex,
    dispatch,
    mockId,
    courseId,
    questionId,
    replied,
    lesson,
    routePrefix = "",
    currentStem,
    selectedChoiceId,
    onInteractiveAnswer,
    isInteractiveAnswerReady,
    isInteractiveAnswering,
  } = props;

  const [isAnswering, setIsAnswering] = useState(false);

  const goToNextQuestionHandler = useCallback(
    () => {
      if (lesson) {
        goToLessonQuestionIndex(dispatch, currentIndex + 1);
      } else {
        goToIndex(dispatch, currentIndex + 1);
      }
    },
    [dispatch, currentIndex, lesson]
  );

  const finishExamMockHandler = useCallback(() => {
    finishMock(dispatch, mockId).then(() => {
      dispatch(replace(`${routePrefix}/mocks/${mockId}/results`));
    });
  }, [dispatch, mockId, routePrefix]);

  const goToPrevQuestionHandler = useCallback(() => {
    if (lesson) {
      goToLessonQuestionIndex(dispatch, currentIndex - 1);
    } else {
      goToIndex(dispatch, currentIndex - 1);
    }
  }, [dispatch, currentIndex, lesson]);

  const answerHandler = useCallback(
    () => {
      setIsAnswering(true);

      let promise;
      if (lesson) {
        if (currentStem && selectedChoiceId) {
          promise = createLessonAnswerV2(
            dispatch,
            lesson,
            questionId,
            currentStem.id,
            selectedChoiceId
          );
        } else {
          promise = createLessonAnswer(dispatch, lesson, replied?.id);
        }
      } else if (currentStem && selectedChoiceId) {
        promise = createMockAnswerV2(
          dispatch,
          courseId,
          mockId,
          questionId,
          currentStem.id,
          selectedChoiceId
        );
      } else {
        promise = createMockAnswer(dispatch, courseId, mockId, questionId, replied?.id);
      }

      if (promise && promise.then) {
        promise
          .then(() => setIsAnswering(false))
          .catch(() => setIsAnswering(false));
      } else {
        setTimeout(() => setIsAnswering(false), 1000);
      }
    },
    [dispatch, courseId, mockId, questionId, replied, lesson, currentStem, selectedChoiceId]
  );

  const effectiveReplied = onInteractiveAnswer
    ? (isInteractiveAnswerReady ? { id: "pending" } : null)
    : replied;

  const effectiveAnswerHandler = onInteractiveAnswer || answerHandler;

  const effectiveIsAnswering = isInteractiveAnswering || isAnswering;

  return (
    <QuestionNavigation
      {...props}
      lesson={lesson}
      finishExamMockHandler={finishExamMockHandler}
      goToNextQuestionHandler={goToNextQuestionHandler}
      goToPrevQuestionHandler={goToPrevQuestionHandler}
      answerHandler={effectiveAnswerHandler}
      replied={effectiveReplied}
      isAnswering={effectiveIsAnswering}
    />
  );
}

function mapStateToProps(state, ownProps) {
  const { lesson } = ownProps;
  const question = lesson ? null : makeCurrentQuestion(state);
  const questionId = question?.id;
  const currentQuestionAndStem = lesson
    ? getCurrentLessonQuestionAndStem(state)
    : getCurrentQuestionAndStem(state);
  const mock = getMock(state);
  const mockId = mock?.id;

  const lessonQuestion = lesson ? currentQuestionAndStem.question : null;
  const finalQuestion = lesson ? lessonQuestion : question;
  const finalQuestionId = finalQuestion?.id;

  const isV2Question = currentQuestionAndStem.stem !== null;

  const replied = lesson
    ? state.lesson.selectedReply
    : isV2Question
      ? null
      : state.mock.selectedReplies[questionId];

  const selectedChoices = lesson
    ? (state.lesson.selectedChoices || {})
    : (state.mock.selectedChoices || {});
  const selectedChoiceId = isV2Question && currentQuestionAndStem.stem
    ? selectedChoices[finalQuestionId]?.[currentQuestionAndStem.stem.id]
    : null;

  const currentIndex = lesson
    ? state.lesson.currentQuestionIndex
    : state.mock.currentIndex;
  const flattenedQuestions = lesson
    ? getFlattenedLessonQuestions(state)
    : getFlattenedQuestionsFromMock(state);
  const questionsLength = flattenedQuestions?.length || 0;
  const nextQuestionItem = flattenedQuestions?.[currentIndex + 1];
  const isLastQuestion = currentIndex >= questionsLength - 1;

  const currentFlattenedEntry = flattenedQuestions?.[currentIndex];
  const isFlowEntry = !!currentFlattenedEntry?.isFlow;

  const flowAnswersForQuestion = (qId) =>
    Object.values(state.entities.answers || {}).filter((a) => {
      if (!a || a.questionPromptId == null) return false;
      if (a.questionId == null || Number(a.questionId) !== Number(qId)) return false;
      if (lesson) {
        return a.lessonId != null && Number(a.lessonId) === Number(lesson.id);
      }
      return a.mockId != null && Number(a.mockId) === Number(mockId);
    });

  const findPromptById = (promptId) => {
    if (promptId == null) return null;
    const questions = Object.values(state.entities.questions || {});
    for (const q of questions) {
      const prompts = q?.meta?.questionPrompts || [];
      const match = prompts.find((p) => Number(p.id) === Number(promptId));
      if (match) return match;
    }
    return null;
  };

  const flowIsCompleteFor = (qId) => {
    const answers = flowAnswersForQuestion(qId);
    return answers.some((a) => findPromptById(a.questionPromptId)?.isFinal === true);
  };

  let isAnswered = false;
  if (isFlowEntry) {
    isAnswered = flowIsCompleteFor(finalQuestionId);
  } else if (isV2Question && currentQuestionAndStem.stem) {
    if (lesson) {
      const answer = getAnswerForLessonQuestionStem(state, {
        questionId: finalQuestionId,
        questionStemId: currentQuestionAndStem.stem.id,
        lessonId: lesson?.id,
      });
      isAnswered = !!answer;
    } else {
      const answer = getAnswerForQuestionStem(state, {
        questionId: finalQuestionId,
        questionStemId: currentQuestionAndStem.stem.id,
        mockId: mockId,
      });
      isAnswered = !!answer;
    }
  } else {
    isAnswered = !!getReplyAnswered(state, {
      questionId: finalQuestionId,
      mockId,
      lessonId: lesson?.id,
    });
  }

  let isNextLocked = false;
  const nextEntry = flattenedQuestions?.[currentIndex + 1];
  // Stems of an interactive question are locked until the flow is completed (rule #6)
  if (nextEntry && !nextEntry.isFlow && nextEntry.question?.format === "interactive") {
    isNextLocked = !flowIsCompleteFor(nextEntry.question?.id);
  }

  let isNextAnswered = false;
  if (nextQuestionItem) {
    if (nextQuestionItem.stem) {
      const nextAnswer = getAnswerForQuestionStem(state, {
        questionId: nextQuestionItem.question?.id,
        questionStemId: nextQuestionItem.stem.id,
        mockId: mockId,
      });
      isNextAnswered = !!nextAnswer;
    } else {
      isNextAnswered = !!getReplyAnswered(state, {
        questionId: nextQuestionItem?.question?.id,
        mockId,
        lessonId: lesson?.id,
      });
    }
  }

  return {
    lesson,
    isLastQuestion,
    questionsLength,
    currentIndex,
    courseId: getCourseFromMock(state)?.id,
    replied: isV2Question ? (selectedChoiceId ? { id: selectedChoiceId } : null) : replied,
    question: finalQuestion,
    currentStem: currentQuestionAndStem.stem,
    selectedChoiceId,
    isNextAnswered,
    isNextLocked,
    isAnswered,
    isFinished: mock?.finishedAt,
    isExam: mock?.exam,
    mockId,
    questionId: finalQuestionId,
    isComplete: makeIsComplete(state),
    hasMockCourses: hasMockCourses(state),
  };
}

export default connect(mapStateToProps)(QuestionNavigationContainer);
